import * as React from 'react';
import { Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Stack } from '@mui/material';
import FlagButton from '../FlagButton';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';


interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
}

const drawerWidth = 240;


export default function NavBar({ ...props}: Props) {
  const { t, i18n } = useTranslation();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const navItems = useMemo(() => {
    return [
      {
        path: "/",
        title: t("navigation.home")
      },
      {
        path: "/about",
        title: t("navigation.about")
      },
      {
        path: "/gallery",
        title: t("navigation.gallery")
      },
      {
        path: "/contact",
        title: t("navigation.contact")
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [i18n.language])

  const drawer = (
    <Box onClick={handleDrawerToggle} flexGrow={1} width="100" textAlign="center">
      <Typography variant="h6" sx={{ my: 2 }}>
        Chandani's Thai kissala
      </Typography>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton sx={{ textAlign: 'center' }} component={Link} to={item.path}>
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box display="flex" id="before" sx={{ bgcolor: 'primary.light', flexGrow: 1, height: '100%' }}>
      <CssBaseline />
      <AppBar component="nav">
        <Toolbar>
          <Stack direction="row" spacing={5} justifyContent="flex-start" sx={{ flexGrow: 1, width: '100%' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              {t("app_name")}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ display: 'flex' }} style={{ marginLeft: 'auto' }}>
              <FlagButton locale="fi" />
              <FlagButton locale="se" />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', sm: 'block' }, width: '30vw', minWidth: 'min-content'  }}>
              <Button component={Link} variant="text" to="/" sx={{ color: '#fff' }}>{t("navigation.home")}</Button>
              <Button component={Link} variant="text" to="/about" sx={{ color: '#fff' }}>{t("navigation.about")}</Button>
              <Button component={Link} variant="text" to="/gallery" sx={{ color: '#fff' }}>{t("navigation.gallery")}</Button>
              <Button component={Link} variant="text" to="/contact" sx={{ color: '#fff' }}>{t("navigation.contact")}</Button>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}